import { Button } from "@/components/Button";
import CopyContent from "@/components/CopyContent";
import OnboardingVideo from "@/components/OnboardingVideo";
import Shimmer from "@/components/Shimmer";
import { HeaderTextBlock } from "@/components/Typography";
import { SplitLayout } from "@/layouts";
import {
  useGetShopifyIntegration,
  useGetWidgetCode,
  usePostUpdateOnboardingProgress,
} from "@/requests/hooks/collect";
import {
  NAVIGATE_FORWARDS,
  SHOPIFY_CHECKOUT_SETTINGS_URL,
} from "@/utils/const";
import { getOnboardingUrl } from "@/utils/onboardingManager";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { tv } from "tailwind-variants";

import additionalScriptsMp4 from "@/assets/videos/collect/onboarding/add-additional-scripts-code.mp4";
import addAllExtensionsMp4 from "@/assets/videos/collect/onboarding/add-all-extensions.mp4";
import addAdditionalScriptsAndCheckoutInformationMp4 from "@/assets/videos/collect/onboarding/add-additional-scripts-code-and-checkout-extension.mp4";
import addThankYouPageMp4 from "@/assets/videos/collect/onboarding/add-order-status-and-thank-you-extensions.mp4";

const styles = tv({
  slots: {
    orderedList:
      "mt-10 flex list-outside list-decimal flex-col gap-10 ps-20 text-start",
    unorderedList: "list-inside list-[lower-alpha]",
    ctaContainer: "w-full sm:w-[unset]",
  },
});

const ShopifyPlacement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoading: isWidgetCodeLoading, data: widgetCodeData } =
    useGetWidgetCode();
  const {
    isLoading: isShopifyIntegrationLoading,
    data: shopifyIntegrationData,
  } = useGetShopifyIntegration();
  const {
    mutate: updateProgress,
    isSuccess: isUpdateSuccess,
    isPending: isUpdateLoading,
  } = usePostUpdateOnboardingProgress();

  const { orderedList, unorderedList, ctaContainer } = styles();

  useEffect(() => {
    if (isUpdateSuccess)
      navigate(getOnboardingUrl("SHOPIFY_WIDGET_INSTALLED"), {
        state: {
          direction: NAVIGATE_FORWARDS,
        },
      });
  }, [isUpdateSuccess]);

  const handleContinue = () => {
    updateProgress({ screen: "SHOPIFY_WIDGET_INSTALLED", flow: "MARKETING" });
  };

  const isShopifyPlus =
    shopifyIntegrationData?.shopifyPlus ||
    shopifyIntegrationData?.checkoutExtensibilityEnabled;
  const isSnippet = !shopifyIntegrationData?.thankYouExtensibilityEnabled;

  const renderPostCheckoutScreenSteps = (
    page: "Thank You" | "Order Status",
  ) => (
    <ul className={unorderedList()}>
      <li>
        <Trans
          i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.standard.step1a"
          values={{ page }}>
          one
          <strong>two</strong>
          three
        </Trans>
      </li>
      <li>
        <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.standard.step1b">
          one
          <strong>two</strong>
          three
        </Trans>
      </li>
      <li>
        <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.standard.step1c">
          one
          <strong>two</strong>
          three
        </Trans>
      </li>
      <li>
        <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.standard.step1d">
          one
          <strong>two</strong>
          three
          <strong>four</strong>
        </Trans>
      </li>
      <li>
        <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.standard.step1e">
          one
          <strong>two</strong>
          three
        </Trans>
      </li>
      <li>
        <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.standard.step1f">
          one
          <strong>two</strong>
          three
        </Trans>
      </li>
    </ul>
  );

  const renderAdditionalScriptsInstructions = () => {
    if (!isSnippet) return null;

    return (
      <li>
        <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.widget.step1">
          one
          <strong>two</strong>
          three
        </Trans>
        <ul className={unorderedList()}>
          <li>
            <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.widget.step2">
              one
              <strong>two</strong>
            </Trans>
          </li>
          <li>
            <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.widget.step3">
              one
              <Button
                trackingId={{
                  eventName:
                    "onboarding.collect.optimize-checkout.add-widget-code",
                }}
                as="a"
                variant="link"
                icon={faCaretRight}
                iconPosition="end"
                iconClassName="w-15 h-15"
                href={
                  widgetCodeData?.shopifyCheckoutSettingsUrl ||
                  SHOPIFY_CHECKOUT_SETTINGS_URL
                }
                width="unset"
                testId="snippet-checkout-settings-link">
                two
              </Button>
            </Trans>
          </li>
          <li>
            <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.widget.step4">
              one
              <strong>two</strong>
              three
              <strong>four</strong>
              five
            </Trans>
          </li>
          <li>
            <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.widget.step5">
              one
              <strong>two</strong>
            </Trans>
          </li>
        </ul>
      </li>
    );
  };

  const renderLinkToCheckoutSettingsStep = () => {
    if (isSnippet && !isShopifyPlus) return null;

    return (
      <li>
        <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.openAdminStep">
          one
          <Button
            trackingId={{
              eventName: "onboarding.collect.optimize-checkout.settings-url",
            }}
            as="a"
            variant="link"
            icon={faCaretRight}
            iconPosition="end"
            iconClassName="w-15 h-15"
            href={
              widgetCodeData?.shopifyCheckoutSettingsUrl ||
              SHOPIFY_CHECKOUT_SETTINGS_URL
            }
            width="unset"
            testId="extensibility-checkout-settings-link">
            two
          </Button>
          three
          <strong>four</strong>
        </Trans>
      </li>
    );
  };

  const renderCheckoutCustomizationStep = () => {
    if (isSnippet && !isShopifyPlus) return null;

    if (isSnippet) {
      return (
        <li>
          <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.openCustomiseStepPostWidget">
            one
            <strong>two</strong>
            three
            <strong>four</strong>
          </Trans>
        </li>
      );
    }

    return (
      <li>
        <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.openCustomiseStep">
          one
          <strong>two</strong>
          three
          <strong>four</strong>
        </Trans>
      </li>
    );
  };

  const renderCheckoutInformationInstructions = () => {
    if (!isShopifyPlus) return null;

    return (
      <li>
        <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.shopifyPlus.step1">
          one
          <strong>two</strong>
          three
        </Trans>
        <ul className={unorderedList()}>
          <li>
            <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.shopifyPlus.step1a">
              one
              <strong>two</strong>
              three
              <strong>four</strong>
            </Trans>
          </li>
          <li>
            <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.shopifyPlus.step1b">
              one
              <strong>two</strong>
              three
            </Trans>
          </li>
          <li>
            <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.shopifyPlus.step1c">
              one
              <strong>two</strong>
              three
            </Trans>
          </li>
          <li>
            <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.shopifyPlus.step1d">
              one
              <strong>two</strong>
              three
            </Trans>
          </li>
        </ul>
      </li>
    );
  };

  const renderThankYouInstructions = () => {
    if (isSnippet) return null;

    return (
      <li>
        <Trans
          i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.standard.step1"
          values={{ page: "Thank You" }}>
          one
          <strong>two</strong>
          three
        </Trans>
        {renderPostCheckoutScreenSteps("Thank You")}
      </li>
    );
  };

  const renderReturnAndContinueStep = () => (
    <li>
      <Trans i18nKey="emailOnboarding.shopifyPlacement.left.header.description.checkoutExtensibility.returnSaveStep">
        one
        <strong>two</strong>
      </Trans>
    </li>
  );

  const renderInstructions = () => (
    <ol className={orderedList()}>
      {renderAdditionalScriptsInstructions()}
      {renderLinkToCheckoutSettingsStep()}
      {renderCheckoutCustomizationStep()}
      {renderCheckoutInformationInstructions()}
      {renderThankYouInstructions()}
      {renderReturnAndContinueStep()}
    </ol>
  );

  const renderDescription = () => {
    if (isShopifyIntegrationLoading || isWidgetCodeLoading)
      return <Shimmer height="220px" />;

    return renderInstructions();
  };

  const getVideoUrl = () => {
    // Snippet only
    if (isSnippet && !isShopifyPlus) return additionalScriptsMp4;
    // CI & Snippet
    if (isSnippet && isShopifyPlus)
      return addAdditionalScriptsAndCheckoutInformationMp4;
    // TY Only
    if (!isSnippet && !isShopifyPlus) return addThankYouPageMp4;
    // CI & TY
    return addAllExtensionsMp4;
  };

  const renderVideo = () => {
    if (isShopifyIntegrationLoading || isWidgetCodeLoading)
      return <Shimmer height="275px" />;

    return <OnboardingVideo videoLocation={getVideoUrl()} />;
  };

  const renderWidgetCodeSection = () => {
    if (isWidgetCodeLoading) return <Shimmer height="118px" width="100%" />;

    if (!widgetCodeData) return null;

    if (!isSnippet) return null;

    return (
      <CopyContent
        content={widgetCodeData?.script}
        trackingId="onboarding.collect.optimize-checkout.copy-widget-code"
        isCode
      />
    );
  };

  const renderButton = () => (
    <div className={ctaContainer()}>
      <Button
        as="button"
        variant="primary"
        onClick={handleContinue}
        loading={isUpdateLoading}
        testId="shopify-placement-cta">
        {t("emailOnboarding.shopifyPlacement.right.cta")}
      </Button>
    </div>
  );

  const renderLeftPanel = () => (
    <HeaderTextBlock
      align="left"
      featureText={t("emailOnboarding.shopifyPlacement.left.header.feature")}
      headingText={t("emailOnboarding.shopifyPlacement.left.header.heading")}
      customHtml={renderDescription()}
      customHtmlPosition="bottom"
    />
  );

  const renderRightPanel = () => (
    <>
      {renderVideo()}
      <div className="mt-30">{renderWidgetCodeSection()}</div>
      <div className="mt-30 flex flex-row justify-end">{renderButton()}</div>
    </>
  );

  return (
    <SplitLayout
      leftPanel={renderLeftPanel()}
      rightPanel={renderRightPanel()}
      backButtonPath={getOnboardingUrl("PRIVACY_POLICY_URL_SAVED")}
    />
  );
};

export default ShopifyPlacement;
